import React from 'react';
import { ArrowRight, Zap, Brain, Network, Sparkles } from 'lucide-react';
import { useHomeDataContext } from '../context/HomeDataContext';
import { useMemo } from 'react';  

const heroImageSrc = `${process.env.PUBLIC_URL || ''}/images/hero.webp`;

const fallbackHero = {
  main_title: 'Intelligence in',
  title: 'Motion',
  sub_title: 'Engineering Intelligence • Empowering the Future',
  description:
    '<p>Ubitz is the convergence of human intellect and artificial cognition. We are the architects of intelligent enterprises &mdash; transforming how organizations think, decide, and evolve.</p>',
  programs_count: '200',
  clients_count: '40',
  experts_count: '5000',
  retention_clients_count: '97',
};

const HeroSection = () => {
  const { data } = useHomeDataContext();


  const hero = useMemo(() => data?.hero ?? fallbackHero, [data]);
  const { main_title,title,sub_title,programs_count,clients_count,experts_count,retention_clients_count, description } = hero;
  
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-blue-50 to-pink-50">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, #0090FF 50px, #0090FF 51px), repeating-linear-gradient(-90deg, transparent, transparent 50px, #0090FF 50px, #0090FF 51px)`,
          backgroundSize: '100% 100%'
        }}></div>
      </div>

      <div className="container mx-auto px-6 lg:px-[7.6923%] py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-pink-100 border border-blue-300 rounded-full shadow-sm">
              <Sparkles style={{ color: '#0090FF' }} size={18} />
              <span style={{ color: '#0D3B77' }} className="text-sm font-semibold">{sub_title}</span>
            </div>
            
            <h1 className="display-huge leading-tight">
              {main_title} <span className="bg-gradient-to-r from-[#0090FF] via-[#E52B7B] to-[#75DA43] bg-clip-text text-transparent"> {title} </span>
            </h1>
            
            <p className="body-large text-gray-700 max-w-xl leading-relaxed"   dangerouslySetInnerHTML={{ __html: description }}>
            </p>

            <div className="flex flex-wrap gap-4">
              <button onClick={scrollToContact} className="btn-primary group">
                Discover the Future
                <ArrowRight className="transition-transform group-hover:translate-x-1" size={20} />
              </button>
              <button onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })} className="btn-secondary">
                Explore Services
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-gray-200">
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-[#0090FF] to-[#0D3B77] bg-clip-text text-transparent">{programs_count}+</div>
                <div className="text-gray-600 text-sm mt-1">Programs</div>
              </div>
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-[#E52B7B] to-[#0090FF] bg-clip-text text-transparent">{clients_count}+</div>
                <div className="text-gray-600 text-sm mt-1">Clients</div>
              </div>
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-[#75DA43] to-[#A2DE3A] bg-clip-text text-transparent">{experts_count}+</div>
                <div className="text-gray-600 text-sm mt-1">Experts</div>
              </div>
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-[#0090FF] to-[#E52B7B] bg-clip-text text-transparent">{retention_clients_count}%</div>
                <div className="text-gray-600 text-sm mt-1">Retention</div>
              </div>
            </div>
          </div>

          {/* Right Side - 3D Digital Figure */}
          <div className="relative h-[500px] lg:h-[650px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
            <img 
              src={heroImageSrc}
              alt="3D Digital Transformation - Intelligence in Motion"
              className="w-full h-full object-cover"
              width="1344"
              height="768"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D3B77]/20 to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Value Pillars */}
      <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-gray-200 py-6 shadow-lg">
        <div className="container mx-auto px-6 lg:px-[7.6923%]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl shadow-sm">
                <Brain style={{ color: '#0090FF' }} size={24} />
              </div>
              <span className="text-gray-800 text-sm font-semibold">Intelligent Strategy</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl shadow-sm">
                <Zap style={{ color: '#E52B7B' }} size={24} />
              </div>
              <span className="text-gray-800 text-sm font-semibold">Digital Transformation</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-100 to-green-200 rounded-xl shadow-sm">
                <Network style={{ color: '#75DA43' }} size={24} />
              </div>
              <span className="text-gray-800 text-sm font-semibold">Cognitive Staffing</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;